import { ReactNode } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { EmptyStateType } from "@/components/ui/empty-state";
import { PaginatedApiResponse } from "@/lib/apiClient";
import { ApiError, Nullable } from "@/types";

// Unified pagination interface that supports both patterns
export interface UnifiedPagination {
  totalPages: number;
  currentPage?: number; // For compatibility with some data-tables
  totalItems?: number; // For compatibility with some data-tables
  pageSize: number;
  total: number;
}

// Convert different pagination formats to unified format
export const normalizePagination = (
  pagination: PaginatedApiResponse<any[]>["pagination"] | UnifiedPagination,
): PaginatedApiResponse<any[]>["pagination"] => {
  // Check if it's already in the correct format
  if ("page" in pagination) {
    return pagination;
  }

  // Convert from UnifiedPagination format
  return {
    totalPages: pagination.totalPages,
    pageSize: pagination.pageSize,
    total: pagination.total,
    page: (pagination as any).currentPage || 0,
  };
};

// Filter configuration interface
export interface FilterConfig {
  component: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  activeCount?: number;
}

// Action button interface
export interface ActionButton {
  label: string;
  icon?: ReactNode;
  // Allow passing an optional argument to onClick handlers (e.g., file upload callbacks)
  onClick: (...args: any[]) => void;
  variant?:
    | "primary"
    | "brand"
    | "secondary"
    | "outline"
    | "ghost"
    | "destructive"
    | "success"
    | "link";
  className?: string;
  disabled?: boolean;
}

// Enhanced DataView props with all features
export interface DataViewProps<TData, TValue> {
  // Data and columns
  columns: ColumnDef<TData, TValue>[];
  data: TData[];

  // Pagination - supports both patterns
  pagination: PaginatedApiResponse<TData[]>["pagination"] | UnifiedPagination;
  pageIndex: number;
  onPageChange?: (pageIndex: number) => void;
  setPageIndex?: React.Dispatch<React.SetStateAction<number>>; // For backward compatibility

  // Loading and error states
  loading: boolean;
  error?: string | null | Nullable<ApiError>;

  // Header content
  title: string;
  description?: string;

  // Search functionality
  searchPlaceholder?: string;
  searchQuery?: string;
  onSearch?: (query: string) => void;

  // Filter functionality
  filterConfig?: FilterConfig;

  // Action buttons
  actionButtons?: ActionButton[];

  // Export/Import functionality
  exportFunc?: () => void;
  importFunc?: () => void;
  showExportImport?: boolean;

  // Row functionality
  onRowClick?: (row: TData) => void;
  enableRowSelection?: boolean;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  enableColumnResizing?: boolean;

  // Table customization
  emptyMessage?: string;
  emptyStateType?: EmptyStateType;
  loadingMessage?: string;
  cardWrapper?: boolean; // Whether to wrap in card style
  cardWrapperClassName?: string;

  // Layout
  className?: string;

  // Status-based content (for dynamic titles/descriptions)
  currentStatus?: string;
  getStatusTitle?: (status?: string) => string;
  getStatusDescription?: (status?: string) => string;

  // Table meta for passing custom data to columns
  meta?: any;
}

// Migration helpers for existing data-table components
export interface LegacyDataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  pagination: UnifiedPagination;
  pageIndex: number;
  setPageIndex: (index: number | ((prev: number) => number)) => void;
  loading?: boolean;
  error?: string | null;
  currentStatus?: string;
  onSearch?: (query: string) => void;
  onFilter?: (filters: any) => void;
  searchQuery?: string;
  onRowClick?: (row: TData) => void;
}

// Convert legacy props to DataView props
export const convertLegacyProps = <TData, TValue>(
  legacyProps: LegacyDataTableProps<TData>,
  additionalProps: Partial<DataViewProps<TData, TValue>> = {},
): DataViewProps<TData, TValue> => {
  const {
    setPageIndex,
    pagination,
    currentStatus,
    // onFilter, // Commented out unused variable
    ...rest
  } = legacyProps;

  return {
    ...rest,
    pagination: normalizePagination(pagination),
    onPageChange: (index: number) => {
      if (typeof setPageIndex === "function") {
        setPageIndex(index);
      }
    },
    title: additionalProps.title || getDefaultTitle(currentStatus),
    description:
      additionalProps.description || getDefaultDescription(currentStatus),
    enableColumnResizing: true,
    cardWrapper: false,
    ...additionalProps,
  } as DataViewProps<TData, TValue>;
};

// Default title/description helpers
const getDefaultTitle = (status?: string): string => {
  if (!status) return "Data";
  return (
    status.charAt(0).toUpperCase() +
    status.slice(1).toLowerCase().replace("_", " ")
  );
};

const getDefaultDescription = (status?: string): string => {
  if (!status) return "Manage your data";
  return `Manage ${status.toLowerCase().replace("_", " ")} data`;
};
